import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, MapPin, Phone, Mail, CreditCard, Edit, Check, AlertCircle } from "lucide-react";

/**
 * Componente per gestire il Proprietario Legale di un animale
 * Il proprietario legale è l'intestatario ufficiale dell'animale
 * (può essere diverso dalla persona che ha creato il record su Zampa)
 */
export default function LegalOwnerSection({ pet, onUpdate, compact = false }) {
  const [showEdit, setShowEdit] = useState(false);
  const legalOwner = pet?.legal_owner || {};
  const hasLegalOwner = legalOwner.name;

  // Compact mode for accordion
  if (compact) {
    return (
      <div data-testid="legal-owner-section-compact">
        {hasLegalOwner ? (
          <div className="space-y-2">
            <div className="flex justify-between items-center py-2 border-b border-[#E5E5EA] dark:border-[#3A3A3C]">
              <span className="text-[#8E8E93] text-sm">Nome</span>
              <span className="text-[#1D1D1F] dark:text-white text-sm font-medium">
                {legalOwner.first_name || legalOwner.name?.split(' ')[0] || '—'} {legalOwner.last_name || legalOwner.name?.split(' ').slice(1).join(' ') || ''}
              </span>
            </div>
            {legalOwner.fiscal_code && (
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5EA] dark:border-[#3A3A3C]">
                <span className="text-[#8E8E93] text-sm">Codice Fiscale</span>
                <span className="text-[#1D1D1F] dark:text-white text-sm font-mono">{legalOwner.fiscal_code}</span>
              </div>
            )}
            {legalOwner.phone && (
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5EA] dark:border-[#3A3A3C]">
                <span className="text-[#8E8E93] text-sm">Telefono</span>
                <span className="text-[#1D1D1F] dark:text-white text-sm">{legalOwner.phone}</span>
              </div>
            )}
            {legalOwner.email && (
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5EA] dark:border-[#3A3A3C]">
                <span className="text-[#8E8E93] text-sm">Email</span>
                <span className="text-[#1D1D1F] dark:text-white text-sm truncate">{legalOwner.email}</span>
              </div>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowEdit(true)}
              className="w-full mt-2 rounded-xl"
            >
              <Edit size={14} className="mr-1" /> Modifica
            </Button>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-[#8E8E93] mb-3">Nessun proprietario legale impostato</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowEdit(true)}
              className="rounded-xl"
            >
              Imposta proprietario
            </Button>
          </div>
        )}
        <LegalOwnerDialog 
          legalOwner={legalOwner}
          open={showEdit} 
          onClose={() => setShowEdit(false)} 
          onSave={(data) => {
            onUpdate({ legal_owner: data });
            setShowEdit(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#1C1C1E] rounded-2xl p-5 shadow-sm border border-black/5 dark:border-white/10" data-testid="legal-owner-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <User className="text-blue-600" size={20} />
          </div>
          <div>
            <h3 className="font-bold text-[#1D1D1F]">Proprietario Legale</h3>
            <p className="text-xs text-[#86868B]">Intestatario ufficiale del documento</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setShowEdit(true)}
          className="text-[#FF6B4A]"
          data-testid="edit-legal-owner-btn"
        >
          <Edit size={16} className="mr-1" />
          {hasLegalOwner ? 'Modifica' : 'Imposta'}
        </Button>
      </div>

      {hasLegalOwner ? (
        <div className="space-y-2">
          {/* Nome e Cognome */}
          <div className="flex items-center gap-3 p-3 bg-[#F5F5F7] rounded-xl">
            <User size={16} className="text-[#86868B] flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-[#86868B]">Nome e Cognome</p>
              <p className="font-semibold text-[#1D1D1F]">
                {legalOwner.first_name && legalOwner.last_name 
                  ? `${legalOwner.first_name} ${legalOwner.last_name}`
                  : legalOwner.name}
              </p>
            </div>
          </div>

          {/* Codice Fiscale */}
          {legalOwner.fiscal_code && (
            <div className="flex items-center gap-3 p-3 bg-[#F5F5F7] rounded-xl">
              <CreditCard size={16} className="text-[#86868B] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-[#86868B]">Codice Fiscale</p>
                <p className="font-mono font-semibold text-[#1D1D1F] text-sm">{legalOwner.fiscal_code}</p>
              </div>
            </div>
          )}

          {/* Indirizzo */}
          {(legalOwner.address || legalOwner.city) && (
            <div className="flex items-center gap-3 p-3 bg-[#F5F5F7] rounded-xl">
              <MapPin size={16} className="text-[#86868B] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-[#86868B]">Indirizzo</p>
                <p className="font-semibold text-[#1D1D1F] text-sm break-words">
                  {[legalOwner.address, legalOwner.city, legalOwner.postal_code].filter(Boolean).join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* Contatti - Stack verticale su mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {legalOwner.phone && (
              <div className="flex items-center gap-2 p-3 bg-[#F5F5F7] rounded-xl">
                <Phone size={14} className="text-[#86868B] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-[#86868B]">Telefono</p>
                  <p className="text-sm font-semibold truncate">{legalOwner.phone}</p>
                </div>
              </div>
            )}
            {legalOwner.email && (
              <div className="flex items-center gap-2 p-3 bg-[#F5F5F7] rounded-xl overflow-hidden">
                <Mail size={14} className="text-[#86868B] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-[#86868B]">Email</p>
                  <p className="text-xs sm:text-sm font-semibold truncate" title={legalOwner.email}>{legalOwner.email}</p>
                </div>
              </div>
            )}
          </div>

          {/* Badge verificato */}
          <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
            <Check size={16} className="text-emerald-600" />
            <span className="text-sm text-emerald-700 font-medium">
              Questi dati appariranno sul Passaporto e Dossier PDF
            </span>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-semibold text-amber-800 text-sm">Proprietario non impostato</p>
              <p className="text-xs text-amber-700 mt-1">
                Imposta il proprietario legale per avere i dati corretti sul Passaporto UE e sul Dossier PDF.
                Il proprietario legale è l'intestatario ufficiale dell'animale.
              </p>
              <Button 
                onClick={() => setShowEdit(true)}
                size="sm"
                className="mt-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
              >
                Imposta proprietario
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <LegalOwnerDialog
        legalOwner={legalOwner}
        open={showEdit}
        onClose={() => setShowEdit(false)}
        onSave={(data) => {
          onUpdate({ legal_owner: data });
          setShowEdit(false);
        }}
      />
    </div>
  );
}


// Dialog per modificare il proprietario legale
function LegalOwnerDialog({ legalOwner, open, onClose, onSave }) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    fiscal_code: "",
    address: "",
    city: "",
    postal_code: "",
    province: "",
    country: "Italia",
    phone: "",
    email: ""
  });

  useEffect(() => {
    if (legalOwner) {
      // Parse existing name into first/last
      const nameParts = (legalOwner.name || "").split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";
      
      setFormData({
        first_name: legalOwner.first_name || firstName,
        last_name: legalOwner.last_name || lastName,
        fiscal_code: legalOwner.fiscal_code || "",
        address: legalOwner.address || "",
        city: legalOwner.city || "",
        postal_code: legalOwner.postal_code || "",
        province: legalOwner.province || "",
        country: legalOwner.country || "Italia",
        phone: legalOwner.phone || "",
        email: legalOwner.email || ""
      });
    }
  }, [legalOwner]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.first_name) {
      toast.error("Il nome è obbligatorio");
      return;
    }
    // Combine first + last name for backwards compat, but also store separately
    const dataToSave = {
      ...formData,
      name: `${formData.first_name} ${formData.last_name}`.trim()
    };
    onSave(dataToSave);
    toast.success("Proprietario legale aggiornato");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[500px] rounded-3xl bg-white p-0 border-0 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-xl font-bold text-[#1D1D1F] flex items-center gap-3">
              <User className="text-[#FF6B4A]" size={24} />
              Proprietario Legale
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-[#86868B] mb-4">
            Il proprietario legale è l'intestatario ufficiale dell'animale. 
            I suoi dati appariranno sul Passaporto UE e sul Dossier PDF.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nome e Cognome separati */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold mb-2 block">Nome *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  placeholder="Es. Mario"
                  className="h-12 rounded-xl bg-[#F5F5F7] border-0"
                  required
                  data-testid="legal-owner-firstname-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-2 block">Cognome</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  placeholder="Es. Rossi"
                  className="h-12 rounded-xl bg-[#F5F5F7] border-0"
                  data-testid="legal-owner-lastname-input"
                />
              </div>
            </div>

            {/* Codice Fiscale */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Codice Fiscale</Label>
              <Input
                value={formData.fiscal_code}
                onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
                placeholder="RSSMRA80A01H501U"
                className="h-12 rounded-xl bg-[#F5F5F7] border-0 font-mono uppercase"
                maxLength={16}
                data-testid="legal-owner-cf-input"
              />
            </div>

            {/* Indirizzo */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Indirizzo</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Via Roma 1"
                className="h-12 rounded-xl bg-[#F5F5F7] border-0"
                data-testid="legal-owner-address-input"
              />
            </div>

            {/* Città, CAP, Provincia */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-1">
                <Label className="text-xs text-[#86868B] mb-1 block">Città</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="Milano"
                  className="h-11 rounded-lg bg-[#F5F5F7] border-0 text-sm"
                  data-testid="legal-owner-city-input"
                />
              </div>
              <div>
                <Label className="text-xs text-[#86868B] mb-1 block">CAP</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  placeholder="20100"
                  className="h-11 rounded-lg bg-[#F5F5F7] border-0 text-sm"
                  maxLength={5}
                  data-testid="legal-owner-cap-input"
                />
              </div>
              <div>
                <Label className="text-xs text-[#86868B] mb-1 block">Provincia</Label>
                <Input
                  value={formData.province}
                  onChange={(e) => setFormData({...formData, province: e.target.value.toUpperCase()})}
                  placeholder="MI"
                  className="h-11 rounded-lg bg-[#F5F5F7] border-0 text-sm uppercase"
                  maxLength={2}
                  data-testid="legal-owner-province-input"
                />
              </div>
            </div>

            {/* Telefono e Email */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold mb-2 block">Telefono</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+39 333 1234567"
                  className="h-12 rounded-xl bg-[#F5F5F7] border-0"
                  data-testid="legal-owner-phone-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@esempio.it"
                  className="h-12 rounded-xl bg-[#F5F5F7] border-0"
                  data-testid="legal-owner-email-input"
                />
              </div>
            </div>

            <Button 
              type="submit"
              className="w-full h-12 bg-[#1D1D1F] rounded-xl"
              data-testid="save-legal-owner-btn"
            >
              Salva proprietario
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
